import { create } from 'zustand';
import { User, UpdateProfileRequest, RegistrationCelebration } from '../types';
import apiService from '../services/api';
import { getErrorMessage } from '../utils/errors';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  registrationCelebration: RegistrationCelebration | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string, bio?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateProfile: (data: UpdateProfileRequest) => Promise<void>;
  clearError: () => void;
  dismissRegistrationCelebration: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  registrationCelebration: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.login({ email, password });
      
      // Store tokens and user data
      localStorage.setItem('accessToken', response.access_token);
      localStorage.setItem('refreshToken', response.refresh_token);
      localStorage.setItem('userId', response.user.id.toString());
      
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
    } catch (error: unknown) {
      set({
        isLoading: false,
        error: getErrorMessage(error, 'Login failed')
      });
      throw error;
    }
  },

  register: async (email: string, password: string, name?: string, bio?: string) => {
    set({ isLoading: true, error: null });
    try {
      const registration = await apiService.register({ email, password, name, bio });
      
      // After registration, automatically log in
      await get().login(email, password);
      set({
        registrationCelebration: {
          countryCode: registration.registrationCountryCode,
          countryRank: registration.registrationCountryRank,
        },
      });
    } catch (error: unknown) {
      set({
        isLoading: false,
        error: getErrorMessage(error, 'Registration failed')
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API error:', error);
    } finally {
      const { useNotificationStore } = await import('./notificationStore');
      useNotificationStore.getState().reset();
      const { useDirectMessageStore } = await import('./directMessageStore');
      useDirectMessageStore.getState().reset();
      const { useRelationshipStore } = await import('./relationshipStore');
      useRelationshipStore.getState().reset();
      // Clear local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        registrationCelebration: null,
      });
    }
  },

  checkAuth: async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      set({ isAuthenticated: false, user: null });
      return;
    }

    set({ isLoading: true });
    try {
      const user = await apiService.getCurrentUser();
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
      
      // Import location store dynamically to avoid circular dependency
      const { useLocationStore } = await import('./locationStore');
      const locationStore = useLocationStore.getState();
      
      // Automatically fetch user's location if they have valid tokens
      await locationStore.fetchUserLocation();
    } catch (error) {
      const { useNotificationStore } = await import('./notificationStore');
      useNotificationStore.getState().reset();
      const { useDirectMessageStore } = await import('./directMessageStore');
      useDirectMessageStore.getState().reset();
      const { useRelationshipStore } = await import('./relationshipStore');
      useRelationshipStore.getState().reset();
      // Token is invalid, clear everything
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
    }
  },

  updateProfile: async (data: UpdateProfileRequest) => {
    set({ isLoading: true, error: null });
    try {
      const updatedUser = await apiService.updateProfile(data);
      set({
        user: updatedUser,
        isLoading: false,
        error: null
      });
    } catch (error: unknown) {
      set({
        isLoading: false,
        error: getErrorMessage(error, 'Failed to update profile')
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  dismissRegistrationCelebration: () => set({ registrationCelebration: null }),
}));
